import { Link, useParams } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { MarketingNav } from "@/components/marketing/MarketingNav";
import { Footer } from "@/components/landing/Footer";
import { LeadForm } from "@/components/marketing/LeadForm";
import { BrowserFrame } from "@/components/marketing/BrowserFrame";
import { LucideIcon } from "@/components/marketing/LucideIcon";
import { SeoHead } from "@/components/SeoHead";
import { INDUSTRIES, INDUSTRY_LIST, SOLUTION_LOGOS } from "@/content/solutions";

const SITE = "https://semalayer.com";

export default function IndustrySolution() {
  const { industry } = useParams();
  const ind = INDUSTRIES[industry];

  if (!ind) {
    return (
      <div data-testid="solution-not-found" className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background text-foreground">
        <p className="font-mono text-sm text-muted-foreground">No solution page for "{industry}".</p>
        <div className="flex flex-wrap justify-center gap-2">
          {INDUSTRY_LIST.map((s) => (
            <Link key={s} to={`/solutions/${s}`} className="rounded-full border border-border/60 px-4 py-1.5 font-mono text-xs uppercase tracking-[0.18em] hover:bg-muted">
              {INDUSTRIES[s].name}
            </Link>
          ))}
        </div>
      </div>
    );
  }

  const accent = ind.accent;
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: ind.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const scrollToLead = () => document.querySelector("#lead")?.scrollIntoView({ behavior: "smooth" });

  return (
    <div data-testid="solution-root" className="relative min-h-screen w-full bg-background text-foreground">
      <SeoHead
        title={ind.metaTitle}
        description={ind.metaDescription}
        keywords={ind.keywords}
        path={`/solutions/${ind.slug}`}
        jsonLd={jsonLd}
      />
      <MarketingNav />

      {/* Hero */}
      <section className="relative overflow-hidden pt-36 pb-20 sm:pt-40">
        <div aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.18]"
          style={{ background: `radial-gradient(60rem 30rem at 70% -10%, ${accent}, transparent)` }} />
        <div className="relative mx-auto grid w-full max-w-6xl grid-cols-1 gap-12 px-6 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <p className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-card/60 px-3 py-1 font-mono text-[11px] uppercase tracking-[0.22em] backdrop-blur"
              style={{ color: accent }}>
              <LucideIcon name={ind.icon} className="h-3.5 w-3.5" /> {ind.eyebrow}
            </p>
            <h1 data-testid="solution-h1" className="mt-6 text-4xl font-semibold tracking-tight sm:text-5xl lg:text-6xl">
              {ind.h1}
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">{ind.subtitle}</p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/signup">
                <Button data-testid="solution-cta-signup" size="lg"
                  className="rounded-full px-6 font-mono text-xs uppercase tracking-[0.2em] text-white hover:opacity-90"
                  style={{ backgroundColor: accent }}>
                  Start free sandbox <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Button data-testid="solution-cta-walkthrough" onClick={scrollToLead} variant="outline" size="lg"
                className="rounded-full border-border/70 px-6 font-mono text-xs uppercase tracking-[0.2em]">
                Book a walkthrough
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Problem band */}
      <section className="border-y border-border/50 bg-card/30 py-16">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">the problem</p>
          <h2 className="mt-3 text-2xl font-semibold tracking-tight sm:text-3xl">{ind.problem.title}</h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">{ind.problem.body}</p>
        </div>
      </section>

      {/* Use cases */}
      <section className="py-24">
        <div className="mx-auto w-full max-w-6xl px-6">
          <p className="font-mono text-[11px] uppercase tracking-[0.22em]" style={{ color: accent }}>use cases</p>
          <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">Questions {ind.name} teams ask Sema.</h2>
          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2">
            {ind.useCases.map((uc) => (
              <div key={uc.title} data-testid={`solution-usecase-${uc.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="rounded-2xl border border-border/60 bg-card/50 p-7 backdrop-blur transition-colors"
                onMouseEnter={(e) => (e.currentTarget.style.borderColor = `${accent}55`)}
                onMouseLeave={(e) => (e.currentTarget.style.borderColor = "")}>
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg"
                  style={{ backgroundColor: `${accent}1f`, color: accent }}>
                  <LucideIcon name={uc.icon} className="h-5 w-5" />
                </span>
                <h3 className="mt-5 text-lg font-semibold tracking-tight">{uc.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{uc.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How Sema solves it — real product screenshot */}
      <section className="border-t border-border/40 py-24">
        <div className="mx-auto grid w-full max-w-6xl grid-cols-1 items-center gap-12 px-6 lg:grid-cols-2 lg:gap-16">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.22em]" style={{ color: accent }}>how sema solves it</p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">Ask in English. Trust the answer.</h2>
            <ul className="mt-8 space-y-4">
              {[
                "Sema learns what every column means and writes the glossary for you.",
                "Define each metric once — it resolves the same way in every answer, report and alert.",
                "Every answer ships with the SQL it ran and the tables it touched.",
                "Sensitive fields are masked or refused by role, and every query is audit-logged.",
              ].map((b) => (
                <li key={b} className="flex items-start gap-3 text-sm leading-relaxed text-foreground/90">
                  <Check className="mt-0.5 h-4 w-4 flex-shrink-0" style={{ color: accent }} strokeWidth={2.4} />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <Link to="/product" className="mt-8 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.18em]" style={{ color: accent }}>
              See how Sema works <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          <BrowserFrame src="/screenshots/chat.png" alt={`Sema answering a ${ind.name} question`}
            caption="Plain-English question → grounded SQL → explainable, charted answer." />
        </div>
      </section>

      {/* Metrics strip */}
      <section className="border-y border-border/50 py-14">
        <div className="mx-auto flex w-full max-w-5xl flex-col items-stretch justify-around gap-8 px-6 sm:flex-row sm:gap-4">
          {ind.metrics.map((m) => (
            <div key={m.label} className="text-center">
              <div className="font-mono text-4xl font-semibold tracking-tight" style={{ color: accent }}>{m.stat}</div>
              <div className="mt-2 text-sm text-muted-foreground">{m.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Logos */}
      <section className="py-16">
        <div className="mx-auto w-full max-w-6xl px-6">
          <p className="text-center font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">trusted by data teams at —</p>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-x-10 gap-y-4 opacity-60">
            {SOLUTION_LOGOS.map((l) => (
              <span key={l} className="font-mono text-sm font-medium tracking-tight text-muted-foreground">{l}</span>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20">
        <div className="mx-auto w-full max-w-3xl px-6">
          <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">questions</p>
          <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">{ind.name}, answered.</h2>
          <Accordion type="single" collapsible className="mt-8" data-testid="solution-faq">
            {ind.faqs.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`}>
                <AccordionTrigger className="text-left text-base">{f.q}</AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-muted-foreground">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Lead capture */}
      <section id="lead" className="border-t border-border/40 py-24">
        <div className="mx-auto grid w-full max-w-6xl grid-cols-1 gap-12 px-6 lg:grid-cols-2 lg:gap-16">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.22em]" style={{ color: accent }}>get started</p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              See Sema on your {ind.name.toLowerCase()} data.
            </h2>
            <p className="mt-4 max-w-md text-base leading-relaxed text-muted-foreground">
              A 20-minute walkthrough against a sample warehouse, then wired to your real schema. No slides.
            </p>
          </div>
          <LeadForm industry={ind.name} accent={accent} source={`solutions/${ind.slug}`} />
        </div>
      </section>

      <Footer />
    </div>
  );
}
